if (!customElements.get('mk-shopping-suite')) {
  customElements.define('mk-shopping-suite', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      this.events = { signal: this.controller.signal };
      this.records = new Map([...this.querySelectorAll('[data-mk-record]')].map(node => [node.dataset.handle, node]));
      this.dialog = this.querySelector('dialog');
      this.items = this.querySelector('[data-mk-items]');
      this.storageOK = true;
      this.state = {};
      this.modes = ['wishlist', 'compare', 'recent'];
      this.modes.forEach(mode => { this.state[mode] = this.read(mode); });
      if (this.dataset.recent === 'true' && this.records.has(this.dataset.current)) {
        this.state.recent = [this.dataset.current, ...this.state.recent.filter(handle => handle !== this.dataset.current)].slice(0, 8);
        this.persist('recent');
      }
      document.addEventListener('click', event => {
        const target = event.target.closest('button');
        if (!target) return;
        if (target.matches('[data-mk-action]')) this.toggle(target);
        else if (target.matches('[data-mk-open]') && this.contains(target)) this.open(target.dataset.mkOpen, target);
        else if (target.matches('[data-mk-close]') && this.contains(target)) this.dialog.close();
        else if (target.matches('[data-mk-clear]') && this.contains(target) && this.mode) {
          this.state[this.mode] = [];
          this.persist(this.mode);
          this.sync();
          this.render();
          this.announce(this.dataset.cleared);
          this.querySelector('[data-mk-close]').focus();
        }
      }, this.events);
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('mk-suite-open');
        if (this.opener?.isConnected) this.opener.focus({ preventScroll: true });
      }, this.events);
      window.addEventListener('storage', event => {
        if (event.key && !event.key.startsWith('mk_shopping_suite_v1:')) return;
        this.modes.forEach(mode => { this.state[mode] = this.read(mode); });
        this.sync();
        if (this.dialog.open) this.render();
      }, this.events);
      document.addEventListener('shopify:section:load', () => this.sync(), this.events);
      window.addEventListener('pageshow', () => {
        this.modes.forEach(mode => { this.state[mode] = this.read(mode); });
        this.sync();
      }, this.events);
      this.hidden = !this.modes.some(mode => this.dataset[mode] === 'true');
      this.sync();
    }
    read(mode) {
      try {
        const value = JSON.parse(localStorage.getItem('mk_shopping_suite_v1:' + mode) || '[]');
        return Array.isArray(value) ? [...new Set(value.filter(handle => typeof handle === 'string' && this.records.has(handle)))].slice(0, mode === 'compare' ? 3 : mode === 'recent' ? 8 : 50) : [];
      } catch (error) {
        this.storageOK = false;
        return this.state?.[mode] || [];
      }
    }
    persist(mode) {
      try { localStorage.setItem('mk_shopping_suite_v1:' + mode, JSON.stringify(this.state[mode])); }
      catch (error) { this.storageOK = false; }
    }
    toggle(button) {
      const handle = button.dataset.handle;
      const mode = button.dataset.mkAction === 'save' ? 'wishlist' : 'compare';
      if (this.dataset[mode] !== 'true' || !this.records.has(handle)) return;
      const selected = this.state[mode].includes(handle);
      if (!selected && this.state[mode].length >= (mode === 'compare' ? 3 : 50)) {
        this.announce(mode === 'compare' ? this.dataset.limit : this.dataset.saveLimit);
        return;
      }
      this.state[mode] = selected ? this.state[mode].filter(item => item !== handle) : [...this.state[mode], handle];
      this.persist(mode);
      this.sync();
      if (this.dialog.open) this.render();
      this.announce(mode === 'wishlist' ? selected ? this.dataset.removed : this.dataset.added : selected ? this.dataset.compareRemoved : this.dataset.compareAdded);
    }
    announce(message) {
      const text = message + (this.storageOK ? '' : ' ' + this.dataset.storageError);
      this.querySelector('[data-mk-notice]').textContent = text;
      this.querySelector('[data-mk-panel-notice]').textContent = text;
    }
    sync() {
      document.querySelectorAll('[data-mk-actions]').forEach(node => {
        node.hidden = this.dataset.wishlist !== 'true' && this.dataset.compare !== 'true';
      });
      document.querySelectorAll('[data-mk-action]').forEach(button => {
        const saving = button.dataset.mkAction === 'save';
        const mode = saving ? 'wishlist' : 'compare';
        const selected = this.state[mode].includes(button.dataset.handle);
        button.hidden = this.dataset[mode] !== 'true';
        button.setAttribute('aria-pressed', String(selected));
        const label = saving ? selected ? this.dataset.saved : this.dataset.save : selected ? this.dataset.comparing : this.dataset.compareLabel;
        const title = this.records.get(button.dataset.handle)?.dataset.title;
        button.setAttribute('aria-label', label + (title ? ': ' + title : ''));
        button.querySelector(saving ? '[data-mk-save-label]' : '[data-mk-compare-label]').textContent = label;
        if (saving) button.querySelector('[aria-hidden]').textContent = selected ? '♥' : '♡';
      });
      this.querySelectorAll('[data-mk-count]').forEach(node => { node.textContent = this.state[node.dataset.mkCount].length; });
      if (!this.storageOK) this.querySelector('[data-mk-storage-note]').textContent = this.dataset.storageError;
    }
    open(mode, opener) {
      if (!this.modes.includes(mode) || this.dataset[mode] !== 'true') return;
      this.mode = mode;
      this.opener = opener;
      this.querySelector('[data-mk-panel-notice]').textContent = '';
      this.render();
      this.dialog.showModal();
      document.documentElement.classList.add('mk-suite-open');
    }
    render() {
      const focused = this.dialog.contains(document.activeElement) ? document.activeElement : null;
      const action = focused?.dataset.mkAction;
      const handle = focused?.dataset.handle;
      const copy = this.querySelector('[data-mk-copy="' + this.mode + '"]');
      this.querySelector('[data-mk-title]').textContent = copy.dataset.title;
      this.querySelector('[data-mk-empty-text]').textContent = copy.dataset.empty;
      this.items.replaceChildren();
      this.items.dataset.mode = this.mode;
      this.state[this.mode].forEach(handle => {
        const record = this.records.get(handle);
        if (record) this.items.append(record.content.cloneNode(true));
      });
      const empty = this.items.childElementCount === 0;
      this.querySelector('[data-mk-empty]').hidden = !empty;
      this.querySelector('[data-mk-clear]').hidden = empty;
      this.querySelector('[data-mk-comparison-note]').hidden = this.mode !== 'compare' || empty;
      this.sync();
      if (action && handle) {
        const replacement = [...this.items.querySelectorAll('[data-mk-action]')].find(button => button.dataset.mkAction === action && button.dataset.handle === handle);
        (replacement || this.querySelector('[data-mk-close]')).focus({ preventScroll: true });
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      if (this.dialog?.open) this.dialog.close();
      document.documentElement.classList.remove('mk-suite-open');
    }
  });
}
